import { useState } from 'react'
import { Loader2, Phone, Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  useActivatePowerDialer,
  useAvailablePDNumbers,
  type AvailablePDNumber,
} from '@/api/powerDialer'
import { usePlanRights } from '@/hooks/usePlanRights'
import { PlanBadge } from '@/components/ui/PlanBadge'
import { trialRefusal } from './trialRefusal'

interface Props {
  onActivated: () => void
}

export function ActivationView({ onActivated }: Props) {
  const { allowed, planFor } = usePlanRights()
  const { t } = useTranslation()
  // areaCodeFilter = code recherché ; areaCode = code effectif passé au hook (déclenche fetch).
  const [areaCodeFilter, setAreaCodeFilter] = useState('')
  const [areaCode, setAreaCode] = useState('')
  const [selected, setSelected] = useState<AvailablePDNumber[]>([])

  const { data: numbers = [], isFetching } = useAvailablePDNumbers({ areaCode })
  const activate = useActivatePowerDialer()

  const toggleNumber = (num: AvailablePDNumber) => {
    setSelected((prev) =>
      prev.find((n) => n.phoneNumber === num.phoneNumber)
        ? prev.filter((n) => n.phoneNumber !== num.phoneNumber)
        : [...prev, num],
    )
  }

  const isSelected = (num: AvailablePDNumber) =>
    selected.some((n) => n.phoneNumber === num.phoneNumber)

  const handleActivate = async () => {
    if (selected.length === 0) return
    try {
      await activate.mutateAsync(selected.map((n) => n.phoneNumber))
      toast.success(
        t('manager.powerDialer.activation.activated', {
          count: selected.length,
        }),
      )
      onActivated()
    } catch (err) {
      toast.error(trialRefusal(err, t) ?? t('manager.powerDialer.activation.error'))
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
            {t('manager.powerDialer.sectionTitle')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('manager.powerDialer.activation.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
        {!allowed('power_dialer') && <PlanBadge plan={planFor('power_dialer')} />}
        <Button
          type="button"
          onClick={() => void handleActivate()}
          disabled={!allowed('power_dialer') || selected.length === 0 || activate.isPending}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {activate.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Phone className="w-4 h-4 mr-2" />
          )}
          {activate.isPending
            ? t('manager.powerDialer.activation.activating')
            : t('manager.powerDialer.activation.activate')}
        </Button>
        </div>
      </div>

      {/* Recherche par indicatif */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder={t('manager.powerDialer.activation.searchPlaceholder')}
            value={areaCodeFilter}
            onChange={(e) => setAreaCodeFilter(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setAreaCode(areaCodeFilter)
            }}
            className="pl-9"
          />
        </div>
        <Button
          type="button"
          onClick={() => setAreaCode(areaCodeFilter)}
          disabled={isFetching}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          {isFetching ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            t('manager.powerDialer.activation.searchButton')
          )}
        </Button>
      </div>

      {/* Table résultats */}
      <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900">
        {isFetching ? (
          <div className="px-4 py-8 text-center">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" />
          </div>
        ) : numbers.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-slate-400">
            {t('manager.powerDialer.activation.noNumbers')}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-800 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider bg-slate-50 dark:bg-slate-800/50">
                <th className="px-4 py-3 w-10" />
                <th className="px-4 py-3">
                  {t('manager.powerDialer.activation.tableNumber')}
                </th>
                <th className="px-4 py-3">
                  {t('manager.powerDialer.activation.tableRegion')}
                </th>
                <th className="px-4 py-3 text-right">
                  {t('manager.powerDialer.activation.tableStatus')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {numbers.map((num) => (
                <tr
                  key={num.phoneNumber}
                  onClick={() => toggleNumber(num)}
                  className={cn(
                    'cursor-pointer transition-colors',
                    isSelected(num)
                      ? 'bg-blue-50 dark:bg-blue-500/10'
                      : 'hover:bg-slate-50 dark:hover:bg-slate-800/50',
                  )}
                >
                  <td className="px-4 py-3">
                    <div
                      className={cn(
                        'w-4 h-4 rounded border-2 flex items-center justify-center',
                        isSelected(num)
                          ? 'bg-blue-600 border-blue-600'
                          : 'border-slate-300 dark:border-slate-600',
                      )}
                    >
                      {isSelected(num) && (
                        <span className="text-white text-[10px]">✓</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-sm font-medium text-slate-900 dark:text-slate-100">
                    {num.phoneNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
                    {num.locality || num.region || 'Canada'}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                      {t('manager.powerDialer.activation.available')}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Sélection */}
      {selected.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden bg-white dark:bg-slate-900">
          <div className="px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/50 flex items-center justify-between">
            <span className="text-sm font-medium text-slate-900 dark:text-white">
              {t('manager.powerDialer.activation.selectedTitle')}
            </span>
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {t('manager.powerDialer.activation.selectedCount', {
                count: selected.length,
              })}
            </span>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {selected.map((num) => (
              <div
                key={num.phoneNumber}
                className="flex items-center justify-between px-4 py-3"
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono text-sm text-slate-900 dark:text-slate-100">
                    {num.phoneNumber}
                  </span>
                  <span className="text-sm text-slate-400">
                    {num.locality || num.region}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => toggleNumber(num)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 bg-blue-500 rounded-full" />
        {t('manager.powerDialer.activation.rotationHint')}
      </p>
    </div>
  )
}
